/**
 * Field PWA — page-level permission hook.
 *
 * Contract:
 *   usePagePermission('warehouse.scan') -> { allowed, isLoading }
 *
 * Resolution order:
 *   1. HOME / SETTINGS / SYNC are always allowed for any authenticated user —
 *      every field role needs the shell chrome and a place to land.
 *   2. Office roles (anything that isn't a `field_*` role — see
 *      `client/utils/roles.ts`) short-circuit to `allowed: true` for every
 *      page. Office users who wander into `/field` (e.g. on a phone) see the
 *      full field app; office identity, not field grants, is what matters
 *      there.
 *   3. Field roles are allowed a page iff their Redux `permissions[]` (from
 *      `/auth/me`, `selectPermissions`) includes that page's required
 *      `feature:*` permission (see PAGE_PERMISSION_MAP below).
 *   4. Bootstrap fallback: if `permissions[]` is empty (mis-seeded account,
 *      race on first load, etc.) but the role is a recognized field role,
 *      fall back to that role's default page allowlist so the app isn't
 *      blank. This is a UX nicety only — the server is the real gate.
 *
 * Page key convention:
 *   <slice>.<page>     e.g. 'warehouse.scan', 'warehouse.receive',
 *                          'jobsite.lookup', 'jobsite.photos'
 *   shared.<page>      e.g. 'shared.home', 'shared.settings', 'shared.sync'
 */

import { useAppSelector } from '../../lib/reduxHooks.js'
import { selectUser, selectRole, selectPermissions } from '../../store/slices/auth.js'
import { selectActiveWarehouseWorker } from '../../store/slices/warehouseWorker.js'
import {
  FIELD_WAREHOUSE,
  FIELD_JOBSITE,
  FIELD_ALL,
  FIELD_PRODUCTION_MANAGER,
  PRODUCTION_STAGE_ROLES,
  isFieldOnly,
} from '../../utils/roles.js'

/** Stable list of page keys used across the field tree. */
export const FIELD_PAGE_KEYS = {
  // Shared
  HOME: 'shared.home',
  SETTINGS: 'shared.settings',
  SYNC: 'shared.sync',
  // Universal "My Activities" surface (card XGiACzr7ZlcP): EVERY field user with
  // activities assigned to them must be able to see them. Ungated — it's in
  // ALWAYS_ALLOWED_PAGE_KEYS below, so it never consults DB feature perms and
  // needs NO seed/grant migration. Role-agnostic by design.
  MY_ACTIVITIES: 'shared.my_activities',
  // Help guides (/field/help, card wZpZCuUW2gmC): read-only, every logged-in
  // user, same rule as the office /help (HELP_SECTION_PLAN.md §4).
  HELP: 'shared.help',

  // Slice A: Warehouse
  WAREHOUSE_SCAN: 'warehouse.scan',
  WAREHOUSE_INVENTORY: 'warehouse.inventory',
  WAREHOUSE_INVENTORY_DETAIL: 'warehouse.inventory.detail',
  WAREHOUSE_RECEIVE: 'warehouse.receive',
  WAREHOUSE_CYCLE_COUNT: 'warehouse.cycle_count',

  // Slice B: Jobsite
  JOBSITE_LOOKUP: 'jobsite.lookup',
  JOBSITE_JOB: 'jobsite.job',
  JOBSITE_LOT: 'jobsite.lot',
  JOBSITE_AREA: 'jobsite.area',
  JOBSITE_PHOTOS: 'jobsite.photos',

  // Slice C: Production department (card aaHMG95zsq83)
  PRODUCTION: 'production.department',
  // Slice D: Field install (installer completion capture + report)
  INSTALL_JOBS: 'install.jobs',
  INSTALL_ACTIVITY: 'install.activity',
  INSTALL_REPORT: 'install.report',
  // B2: lot/area completion view (read-only lot + assigned areas).
  INSTALL_LOT: 'install.lot',
} as const

export type FieldPageKey = (typeof FIELD_PAGE_KEYS)[keyof typeof FIELD_PAGE_KEYS]

/** Page keys that never require a permission — any authenticated user gets them. */
const ALWAYS_ALLOWED_PAGE_KEYS: ReadonlySet<string> = new Set([
  FIELD_PAGE_KEYS.HOME,
  FIELD_PAGE_KEYS.SETTINGS,
  FIELD_PAGE_KEYS.SYNC,
  // "My Activities" is the universal assignment surface — any authenticated
  // field user must reach it regardless of role/department (card XGiACzr7ZlcP).
  FIELD_PAGE_KEYS.MY_ACTIVITIES,
  FIELD_PAGE_KEYS.HELP,
])

/**
 * Warehouse-worker session scope (card WH-5, 2026-09-10 client meeting).
 *
 * When a production worker is PIN'd in on the shared warehouse tablet (the
 * `warehouseWorker` slice is active), the app must be CONFINED to warehouse
 * floor work regardless of what the underlying shared office/field account can
 * see. Katia (meeting): "nothing in the bottom [nav]… they're definitely not
 * going to have the inventory one." So a PIN'd worker gets ONLY:
 *   - the shell chrome (HOME / SETTINGS / SYNC — also in ALWAYS_ALLOWED),
 *   - the warehouse hub landing (via WAREHOUSE_HUB_PAGE_KEYS "any" gating),
 *   - their claim-based my-work flow (also gated on the hub keys).
 * Everything else — Inventory, jobsite, install, production, admin/office nav —
 * is denied for the PIN'd session. This is enforced at the single resolver
 * choke point below, so BOTH nav rendering (FieldTabBar / WarehouseHome /
 * HomePicker) and the route guards (RequirePermission / RequireAnyPermission)
 * honor it without duplicating the rule.
 *
 * NOTE (needs Noah): this deliberately EXCLUDES warehouse Inventory per Katia's
 * explicit ask. Scan / Receive / Cycle count / Job lookup are the warehouse
 * hub's other tiles — they remain visible to a PIN'd worker here (they're
 * warehouse floor actions). If a worker should also lose those, trim this set.
 */
const WAREHOUSE_WORKER_ALLOWED_PAGE_KEYS: ReadonlySet<string> = new Set<string>([
  FIELD_PAGE_KEYS.HOME,
  FIELD_PAGE_KEYS.SETTINGS,
  FIELD_PAGE_KEYS.SYNC,
  // Help is read-only shell chrome like Settings: the header "?" shows on every
  // page, so a PIN'd worker must be able to open it.
  FIELD_PAGE_KEYS.HELP,
  FIELD_PAGE_KEYS.WAREHOUSE_SCAN,
  FIELD_PAGE_KEYS.WAREHOUSE_RECEIVE,
  FIELD_PAGE_KEYS.WAREHOUSE_CYCLE_COUNT,
  // Job lookup is a warehouse hub tile (find a lot/job) — a floor action, kept.
  FIELD_PAGE_KEYS.JOBSITE_LOOKUP,
])

/**
 * Canonical pageKey -> required `feature:*` permission. Must match the seed
 * in `supabase/migrations/20260803000000_seed_field_roles_and_permissions.sql`
 * (Card A) exactly — this is the client-side mirror of that grant map.
 */
const PAGE_PERMISSION_MAP: Partial<Record<FieldPageKey, string>> = {
  [FIELD_PAGE_KEYS.WAREHOUSE_SCAN]: 'feature:warehouse_scan',
  [FIELD_PAGE_KEYS.WAREHOUSE_INVENTORY]: 'feature:warehouse_inventory',
  [FIELD_PAGE_KEYS.WAREHOUSE_INVENTORY_DETAIL]: 'feature:warehouse_inventory',
  [FIELD_PAGE_KEYS.WAREHOUSE_RECEIVE]: 'feature:warehouse_receive',
  // Cycle count reconciles physical vs. system inventory for a section — it
  // reuses the warehouse_inventory grant (same warehouse-worker capability;
  // no separate feature perm is seeded for it).
  [FIELD_PAGE_KEYS.WAREHOUSE_CYCLE_COUNT]: 'feature:warehouse_inventory',
  [FIELD_PAGE_KEYS.JOBSITE_LOOKUP]: 'feature:jobsite_lookup',
  [FIELD_PAGE_KEYS.JOBSITE_JOB]: 'feature:jobsite_job',
  [FIELD_PAGE_KEYS.JOBSITE_LOT]: 'feature:jobsite_lot',
  [FIELD_PAGE_KEYS.JOBSITE_AREA]: 'feature:jobsite_area',
  [FIELD_PAGE_KEYS.JOBSITE_PHOTOS]: 'feature:jobsite_photos',
  // Install capture rides on the jobsite grant set — an installer is a
  // jobsite-side field worker (areas + selections only, never pricing/POs,
  // per BR11). Reusing the jobsite feature perms means field_jobsite and
  // field_all accounts get install access without a new seed migration.
  [FIELD_PAGE_KEYS.INSTALL_JOBS]: 'feature:jobsite_lookup',
  [FIELD_PAGE_KEYS.INSTALL_ACTIVITY]: 'feature:jobsite_area',
  [FIELD_PAGE_KEYS.INSTALL_REPORT]: 'feature:jobsite_lookup',
  // The lot completion view shows a lot's areas + selections — same read
  // capability as the jobsite lot page.
  [FIELD_PAGE_KEYS.INSTALL_LOT]: 'feature:jobsite_lot',
  // PRODUCTION is intentionally NOT in this 1:1 map — the production page is
  // reachable by ANY production stage role (each holds a distinct
  // feature:production_<stage> perm) plus the manager (feature:production_manage).
  // That "any of a set" rule is handled specially in usePagePermission below.
}

/** True if any of the user's perms is a production feature perm. */
const PRODUCTION_FEATURE_PREFIX = 'feature:production_'
function hasAnyProductionFeature(permissions: readonly string[]): boolean {
  return permissions.some((p) => p.startsWith(PRODUCTION_FEATURE_PREFIX))
}

/**
 * Bootstrap fallback allowlist per field role, used only when
 * `permissions[]` is empty. Mirrors the Card A seed's role -> feature-perm
 * grants (see the map above) so a mis-seeded account still gets a usable
 * (if permissive) app rather than a blank one.
 */
const FALLBACK_ALLOWLIST: Record<string, ReadonlySet<FieldPageKey>> = {
  [FIELD_WAREHOUSE]: new Set([
    FIELD_PAGE_KEYS.WAREHOUSE_SCAN,
    FIELD_PAGE_KEYS.WAREHOUSE_INVENTORY,
    FIELD_PAGE_KEYS.WAREHOUSE_INVENTORY_DETAIL,
    FIELD_PAGE_KEYS.WAREHOUSE_RECEIVE,
    FIELD_PAGE_KEYS.WAREHOUSE_CYCLE_COUNT,
  ]),
  [FIELD_JOBSITE]: new Set([
    FIELD_PAGE_KEYS.JOBSITE_LOOKUP,
    FIELD_PAGE_KEYS.JOBSITE_JOB,
    FIELD_PAGE_KEYS.JOBSITE_LOT,
    FIELD_PAGE_KEYS.JOBSITE_AREA,
    FIELD_PAGE_KEYS.JOBSITE_PHOTOS,
    FIELD_PAGE_KEYS.INSTALL_JOBS,
    FIELD_PAGE_KEYS.INSTALL_ACTIVITY,
    FIELD_PAGE_KEYS.INSTALL_REPORT,
    FIELD_PAGE_KEYS.INSTALL_LOT,
  ]),
  [FIELD_ALL]: new Set([
    FIELD_PAGE_KEYS.WAREHOUSE_SCAN,
    FIELD_PAGE_KEYS.WAREHOUSE_INVENTORY,
    FIELD_PAGE_KEYS.WAREHOUSE_INVENTORY_DETAIL,
    FIELD_PAGE_KEYS.WAREHOUSE_RECEIVE,
    FIELD_PAGE_KEYS.WAREHOUSE_CYCLE_COUNT,
    FIELD_PAGE_KEYS.JOBSITE_LOOKUP,
    FIELD_PAGE_KEYS.JOBSITE_JOB,
    FIELD_PAGE_KEYS.JOBSITE_LOT,
    FIELD_PAGE_KEYS.JOBSITE_AREA,
    FIELD_PAGE_KEYS.JOBSITE_PHOTOS,
    FIELD_PAGE_KEYS.INSTALL_JOBS,
    FIELD_PAGE_KEYS.INSTALL_ACTIVITY,
    FIELD_PAGE_KEYS.INSTALL_REPORT,
    FIELD_PAGE_KEYS.INSTALL_LOT,
  ]),
  // Every production stage role + the manager land on the production page (+
  // the jobsite pages they can read). Built from the shared role list so a new
  // stage role needs no change here.
  ...Object.fromEntries(
    [...PRODUCTION_STAGE_ROLES, FIELD_PRODUCTION_MANAGER].map((roleName) => [
      roleName,
      new Set<FieldPageKey>([
        FIELD_PAGE_KEYS.PRODUCTION,
        FIELD_PAGE_KEYS.JOBSITE_JOB,
        FIELD_PAGE_KEYS.JOBSITE_LOT,
        FIELD_PAGE_KEYS.JOBSITE_AREA,
      ]),
    ]),
  ),
}

export interface PagePermissionResult {
  /** Whether the current user is allowed to see this page. */
  allowed: boolean
  /** True while we're still figuring out the answer. */
  isLoading: boolean
  /** True when the user isn't logged in at all. Distinguishes "log in" vs "denied". */
  isUnauthenticated: boolean
}

/** The slice of auth state the pure resolver needs. */
interface AuthContext {
  user: ReturnType<typeof selectUser>
  role: ReturnType<typeof selectRole>
  permissions: readonly string[]
  /** True when a warehouse worker is PIN'd in on this shared tablet (WH-5). */
  isWarehouseWorkerSession: boolean
}

/**
 * Pure resolution of page access from a snapshot of auth state. Extracted from
 * the hook so it can be reused by `useAnyPagePermission` (which resolves many
 * page keys against ONE Redux read) without calling `usePagePermission` in a
 * loop — that would violate rules-of-hooks. See the module doc comment for the
 * resolution order.
 */
function resolvePagePermission(pageKey: string, ctx: AuthContext): PagePermissionResult {
  const { user, role, permissions, isWarehouseWorkerSession } = ctx
  const isUnauthenticated = !user

  if (isUnauthenticated) {
    return { allowed: false, isLoading: false, isUnauthenticated: true }
  }

  // WH-5: a PIN'd warehouse-worker session is CONFINED to the warehouse floor
  // scope, regardless of the shared account's role/permissions. This runs
  // BEFORE every other rule (incl. the office/admin short-circuit) so a
  // warehouse tablet signed into an office/admin account still can't reach
  // Inventory, jobsite, install, production, or admin nav while a worker is
  // PIN'd in. Denied pages fail closed → nav hides them and the route guards
  // bounce a direct URL to /field/home.
  if (isWarehouseWorkerSession) {
    return {
      allowed: WAREHOUSE_WORKER_ALLOWED_PAGE_KEYS.has(pageKey),
      isLoading: false,
      isUnauthenticated: false,
    }
  }

  // Always-allowed shared pages — no permission needed.
  if (ALWAYS_ALLOWED_PAGE_KEYS.has(pageKey)) {
    return { allowed: true, isLoading: false, isUnauthenticated: false }
  }

  const roleName = role?.name ?? null

  // Office / admin short-circuit: anyone NOT sequestered to the field app
  // (effective app_access = 'both', incl. admins/dev-emails) gets full access
  // to every field page. Only field-only users fall through to per-feature
  // gating below. Keyed off the server-resolved `app_access` UNION (card
  // BnkVHTRg-Aih) rather than the role name, so a `field_*`-named role flipped
  // to 'both' correctly sees everything, and any role flipped to 'field_only'
  // is gated. (`isFieldOnly` returns false for admins.)
  if (!isFieldOnly(role)) {
    return { allowed: true, isLoading: false, isUnauthenticated: false }
  }

  // Production page — "any production feature perm" grants it (each stage role
  // holds a distinct feature:production_<stage>; the manager holds
  // feature:production_manage). Falls back to the role allowlist when
  // permissions[] hasn't loaded yet.
  if (pageKey === FIELD_PAGE_KEYS.PRODUCTION) {
    if (permissions.length > 0) {
      return {
        allowed: hasAnyProductionFeature(permissions),
        isLoading: false,
        isUnauthenticated: false,
      }
    }
    const prodFallback = roleName ? FALLBACK_ALLOWLIST[roleName] : undefined
    return {
      allowed: prodFallback ? prodFallback.has(FIELD_PAGE_KEYS.PRODUCTION) : false,
      isLoading: false,
      isUnauthenticated: false,
    }
  }

  const requiredPermission = PAGE_PERMISSION_MAP[pageKey as FieldPageKey]
  // No mapping declared for this pageKey — fail closed for field roles.
  if (!requiredPermission) {
    return { allowed: false, isLoading: false, isUnauthenticated: false }
  }

  if (permissions.length > 0) {
    return {
      allowed: permissions.includes(requiredPermission),
      isLoading: false,
      isUnauthenticated: false,
    }
  }

  // Bootstrap fallback — permissions[] hasn't been seeded/loaded yet. Use
  // the role's default allowlist so the account isn't blank. The server is
  // still the real gate; this is UX only.
  const fallback = roleName ? FALLBACK_ALLOWLIST[roleName] : undefined
  return {
    allowed: fallback ? fallback.has(pageKey as FieldPageKey) : false,
    isLoading: false,
    isUnauthenticated: false,
  }
}

/**
 * Reads the current auth snapshot from Redux. A single hook so both
 * `usePagePermission` and `useAnyPagePermission` gather state the same way and
 * then delegate to the pure resolver.
 */
function useAuthContext(): AuthContext {
  const user = useAppSelector(selectUser)
  const role = useAppSelector(selectRole)
  const permissions = useAppSelector(selectPermissions)
  const activeWarehouseWorker = useAppSelector(selectActiveWarehouseWorker)
  return { user, role, permissions, isWarehouseWorkerSession: Boolean(activeWarehouseWorker) }
}

/**
 * Resolves page access from the current user's role + permissions.
 *
 * See the module doc comment above for the resolution order. The hook
 * signature is unchanged from the Phase 0 stub — `FieldTabBar`, `routes.tsx`
 * `gated()`, and `HomePicker` need no changes; they already consume this via
 * `{ allowed }` and auto-filter.
 */
export function usePagePermission(pageKey: string): PagePermissionResult {
  return resolvePagePermission(pageKey, useAuthContext())
}

/**
 * Hub-tab gating: a hub bottom-tab (Warehouse, Production) should appear when
 * the user can see AT LEAST ONE of the child pages it nests. Resolves every
 * child page key against a SINGLE Redux read via the pure `resolvePagePermission`
 * — no hooks in a loop, so it's rules-of-hooks safe for any-length lists.
 *
 * The child buttons still gate themselves individually via `usePagePermission`,
 * so a role that can see the hub but not a given action never sees that
 * action's button.
 */
export function useAnyPagePermission(pageKeys: readonly FieldPageKey[]): PagePermissionResult {
  const ctx = useAuthContext()
  const results = pageKeys.map((key) => resolvePagePermission(key, ctx))
  return {
    allowed: results.some((r) => r.allowed),
    isLoading: results.some((r) => r.isLoading),
    // Unauthenticated only if EVERY child says so (i.e. there's no user at all).
    isUnauthenticated: results.length > 0 && results.every((r) => r.isUnauthenticated),
  }
}

/** Child page keys nested under the Warehouse hub tab. */
export const WAREHOUSE_HUB_PAGE_KEYS: readonly FieldPageKey[] = [
  FIELD_PAGE_KEYS.WAREHOUSE_SCAN,
  FIELD_PAGE_KEYS.WAREHOUSE_RECEIVE,
  FIELD_PAGE_KEYS.WAREHOUSE_INVENTORY,
  FIELD_PAGE_KEYS.WAREHOUSE_CYCLE_COUNT,
  FIELD_PAGE_KEYS.JOBSITE_LOOKUP,
]

/** Child page keys nested under the Production hub tab. */
export const PRODUCTION_HUB_PAGE_KEYS: readonly FieldPageKey[] = [
  FIELD_PAGE_KEYS.PRODUCTION,
  FIELD_PAGE_KEYS.INSTALL_JOBS,
]
